import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const CycleCountsList = forwardRef(({ onSelectCount, selectedCountId, onNew }, ref) => {
  const { t } = useTranslation('warehouse');
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchCounts
  }));

  useEffect(() => {
    fetchCounts();
  }, [statusFilter, typeFilter]);

  const fetchCounts = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/cycle-counts?limit=200');
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (typeFilter !== 'all') {
        url += `&count_type=${typeFilter}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setCounts(data);
      } else {
        setError(t('cycleCounts.error.fetchFailed', 'Failed to fetch cycle counts'));
      }
    } catch (error) {
      console.error('Error fetching cycle counts:', error);
      setError(t('cycleCounts.error.loadingError', 'Error loading cycle counts'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCounts = counts.filter(count => {
    const matchesSearch = (
      (count.count_number && count.count_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (count.location && count.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (count.zone && count.zone.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (count.counter_name && count.counter_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return matchesSearch;
  });

  const getStatusBadge = (status) => {
    const statusMap = {
      'SCHEDULED': 'status-warning',
      'IN_PROGRESS': 'status-active',
      'COMPLETED': 'status-success',
      'APPROVED': 'status-inactive'
    };
    const statusStyle = statusMap[status] || 'status-inactive';
    return (
      <span className={`status-badge ${statusStyle}`}>
        {t(`cycleCounts.status.${status.toLowerCase()}`, status)}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatAppDate(dateStr);
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && counts.length === 0) {
    return <div className="list-container"><div className="loading">{t('cycleCounts.loading', 'Loading cycle counts...')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('cycleCounts.title', 'Cycle Counts')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('cycleCounts.newCount', 'New Count')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('cycleCounts.searchPlaceholder', 'Search counts...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('cycleCounts.filters.allStatus', 'All Status')}</option>
            <option value="SCHEDULED">{t('cycleCounts.status.scheduled', 'Scheduled')}</option>
            <option value="IN_PROGRESS">{t('cycleCounts.status.in_progress', 'In Progress')}</option>
            <option value="COMPLETED">{t('cycleCounts.status.completed', 'Completed')}</option>
            <option value="APPROVED">{t('cycleCounts.status.approved', 'Approved')}</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('cycleCounts.filters.allTypes', 'All Types')}</option>
            <option value="CYCLE">{t('cycleCounts.types.cycle', 'Cycle')}</option>
            <option value="SPOT">{t('cycleCounts.types.spot', 'Spot')}</option>
            <option value="FULL">{t('cycleCounts.types.full', 'Full')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('cycleCounts.tableHeaders.number', '#')}</th>
              <th>{t('cycleCounts.tableHeaders.type', 'Type')}</th>
              <th>{t('cycleCounts.tableHeaders.location', 'Location')}</th>
              <th>{t('cycleCounts.tableHeaders.scheduledDate', 'Date')}</th>
              <th>{t('cycleCounts.tableHeaders.items', 'Items')}</th>
              <th>{t('cycleCounts.tableHeaders.status', 'Status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredCounts.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {searchTerm ? t('cycleCounts.noData.searching', 'No counts found matching your search') : t('cycleCounts.noData.empty', 'No cycle counts found')}
                </td>
              </tr>
            ) : (
              filteredCounts.map(count => (
                <tr
                  key={count.cycle_count_id}
                  onClick={() => onSelectCount(count.cycle_count_id)}
                  className={selectedCountId === count.cycle_count_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">{count.count_number}</span>
                  </td>
                  <td>
                    <span className="dept-code">{count.count_type}</span>
                  </td>
                  <td>
                    <strong>{count.location || '-'}</strong>
                    {count.zone && <span style={{ color: 'var(--text-secondary)', marginLeft: '4px' }}>({count.zone})</span>}
                  </td>
                  <td>
                    {formatDate(count.scheduled_date || count.count_date)}
                  </td>
                  <td>
                    {count.item_count || 0}
                  </td>
                  <td>
                    {getStatusBadge(count.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default CycleCountsList;
